import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
  CreditCard,
  QrCode,
  ExternalLink,
  Copy,
  Trash2,
  Loader2,
  Receipt,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const SITUACAO = {
  open: { rotulo: "Em aberto", variante: "outline-solid" },
  paid: { rotulo: "Paga", variante: "secondary" },
  overdue: { rotulo: "Atrasada", variante: "destructive" },
  canceled: { rotulo: "Cancelada", variante: "outline-solid" },
};

const CICLO = {
  monthly: "Mensal",
  quarterly: "Trimestral",
  yearly: "Anual",
};

// A cobrança nem sempre é em real: pelo Stripe ela sai na moeda configurada,
// e cada fatura guarda a sua — trocar a moeda hoje não reescreve o que já foi
// cobrado ontem.
const moeda = (valor, codigo) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: codigo || "BRL",
  });

// Vencimento vem do banco como meia-noite UTC. Em fuso negativo, converter
// para hora local joga a data para o dia anterior — por isso a parte da data
// é lida literalmente, sem passar por Date.
const dataCurta = (valor) => {
  if (!valor) return "—";

  const soData = String(valor).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(soData)) {
    const [ano, mes, dia] = soData.split("-");
    return `${dia}/${mes}/${ano}`;
  }

  try {
    return format(parseISO(valor), "dd/MM/yyyy");
  } catch {
    return "—";
  }
};

// Cartão de resumo do topo. Só texto e um rótulo — sem gráfico, porque aqui
// o que importa é o número exato, não a tendência.
const Resumo = ({ rotulo, valor, detalhe }) => (
  <div className="rounded-xl border bg-card p-4">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {rotulo}
    </p>
    <p className="mt-1 text-xl font-semibold tabular-nums">{valor}</p>
    {detalhe && (
      <p className="mt-1 text-xs text-muted-foreground">{detalhe}</p>
    )}
  </div>
);

const Billing = () => {
  const [assinatura, setAssinatura] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pagando, setPagando] = useState(null);
  const [confirmRemover, setConfirmRemover] = useState(false);
  const [gerandoPagamento, setGerandoPagamento] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [resAssinatura, resFaturas] = await Promise.all([
        api.get("/subscriptions/mine"),
        api.get("/invoices/mine"),
      ]);
      setAssinatura(resAssinatura.data);
      setInvoices(resFaturas.data.invoices || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Volta da página do Stripe. O aviso de pagamento chega pelo webhook, que
  // pode demorar alguns segundos — por isso o texto não afirma que já baixou.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("cartao") === "salvo") {
      toast.success("Cartão cadastrado.");
      // Quem grava o cartão aqui é o webhook do Stripe, logo depois. Recarrega
      // para o cartão aparecer sem a pessoa precisar dar F5.
      setTimeout(carregar, 4000);
    }

    if (params.get("pagamento") === "sucesso") {
      toast.success("Pagamento recebido. A fatura baixa em instantes.");
    }

    if (params.get("cartao") || params.get("pagamento")) {
      // Limpa o endereço para o aviso não voltar a cada F5.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [carregar]);

  const billing = assinatura?.billing || {};

  const copiar = async (texto, mensagem) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(mensagem);
    } catch {
      toast.error("Não consegui copiar. Selecione o texto e copie na mão.");
    }
  };

  // Pagar sem esperar a cobrança chegar. Se já existe fatura em aberto é ela
  // que abre — clicar aqui não pode virar uma segunda dívida.
  const pagarAgora = async () => {
    setGerandoPagamento(true);
    try {
      const { data } = await api.post("/subscription/pay");

      // O diálogo é o mesmo do botão Pagar de cada linha; só o nome do campo
      // muda entre a rota nova e a listagem.
      setPagando({
        ...data,
        invoiceUrl: data.url,
        detail: "Mensalidade",
      });

      carregar();
    } catch (err) {
      toastError(err);
    }
    setGerandoPagamento(false);
  };

  const alternarDebito = async (ativo) => {
    try {
      const { data } = await api.put("/subscription/auto-charge", { ativo });
      setAssinatura((atual) => ({
        ...atual,
        billing: { ...atual.billing, autoCharge: data.autoCharge },
      }));
      toast.success(
        data.autoCharge
          ? "Débito automático ligado. As próximas faturas serão cobradas no cartão."
          : "Débito automático desligado. Você paga cada fatura na mão."
      );
    } catch (err) {
      toastError(err);
    }
  };

  const removerCartao = async () => {
    try {
      await api.delete("/subscription/card");
      setAssinatura((atual) => ({
        ...atual,
        billing: {
          ...atual.billing,
          cardLastDigits: null,
          cardBrand: null,
          autoCharge: false,
        },
      }));
      toast.success("Cartão removido.");
    } catch (err) {
      toastError(err);
    }
  };

  const emAberto = invoices.filter(
    (i) => i.status === "open" || i.status === "overdue"
  );
  const totalEmAberto = emAberto.reduce((s, i) => s + Number(i.value || 0), 0);

  return (
    <MainContainer>
      <ConfirmationModal
        title="Remover o cartão salvo?"
        open={confirmRemover}
        onClose={() => setConfirmRemover(false)}
        onConfirm={removerCartao}
      >
        O débito automático desliga junto. As próximas faturas continuam sendo
        geradas — só que você passa a pagar cada uma por Pix ou boleto.
      </ConfirmationModal>

      <MainHeader>
        <div className="min-w-0 flex-1">
          <Title>Financeiro</Title>
        </div>
        <MainHeaderButtonsWrapper>
          <Button onClick={pagarAgora} disabled={gerandoPagamento}>
            {gerandoPagamento ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Receipt className="h-4 w-4" />
            )}
            Pagar agora
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 space-y-6 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Seu plano e suas faturas. Dá para pagar na hora, sem falar com
          ninguém: o botão <strong>Pagar agora</strong> abre a página segura
          {billing.gateway === "stripe"
            ? " do Stripe, com todas as formas de pagamento aceitas."
            : " do Asaas, com Pix e boleto."}
          {" "}Não precisa esperar a cobrança chegar.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Resumo
            rotulo="Plano"
            valor={assinatura?.planName || "—"}
            detalhe={CICLO[assinatura?.billingCycle] || null}
          />
          <Resumo
            rotulo="Mensalidade"
            valor={moeda(assinatura?.total, billing.currency)}
            detalhe="Plano, módulos e limites extras já somados"
          />
          <Resumo
            rotulo="Próximo vencimento"
            valor={dataCurta(billing.dueDate)}
            detalhe={
              billing.companyStatus === "blocked"
                ? "Acesso bloqueado por falta de pagamento"
                : null
            }
          />
          <Resumo
            rotulo="Em aberto"
            valor={moeda(totalEmAberto, billing.currency)}
            detalhe={`${emAberto.length} fatura(s) a pagar`}
          />
        </div>

        {/* Cartão salvo.
            Não há mais como cadastrar um: pagar é sempre pela página do
            gateway, que aceita todas as formas — inclusive as que não podem
            ser guardadas, como MB WAY e Multibanco.
            Este bloco só existe para quem cadastrou antes: sem ele, essa
            pessoa ficaria com um cartão sendo debitado todo mês e nenhum
            lugar para desligar. */}
        {billing.cardLastDigits && (
          <div className="rounded-xl border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Cartão salvo</p>
                  <p className="text-sm text-muted-foreground">
                    {`${billing.cardBrand || "Cartão"} terminado em ${billing.cardLastDigits}`}
                  </p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label="Remover cartão salvo"
                title="Remover cartão salvo"
                onClick={() => setConfirmRemover(true)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-4 flex items-center gap-3 border-t pt-4">
              <Switch
                id="auto-charge"
                checked={Boolean(billing.autoCharge)}
                onCheckedChange={alternarDebito}
              />
              <Label htmlFor="auto-charge" className="cursor-pointer">
                Cobrar no cartão automaticamente todo vencimento
                <span className="ml-1 font-normal text-muted-foreground">
                  — evita esquecer e o sistema bloquear.
                </span>
              </Label>
            </div>
          </div>
        )}

        {/* Faturas */}
        <div className="overflow-x-auto rounded-xl border bg-card">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow>
                <TableHead>Do que se trata</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-center">Vence em</TableHead>
                <TableHead className="text-center">Situação</TableHead>
                <TableHead className="text-center">Pagar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => {
                const situacao = SITUACAO[invoice.status] || SITUACAO.open;
                const pagavel =
                  invoice.status === "open" || invoice.status === "overdue";

                return (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      {invoice.detail}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {moeda(invoice.value, invoice.currency)}
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      {dataCurta(invoice.dueDate)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={situacao.variante}>
                        {situacao.rotulo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {pagavel &&
                      (invoice.pixPayload || invoice.invoiceUrl) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPagando(invoice)}
                        >
                          <QrCode className="h-4 w-4" />
                          Pagar
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {pagavel ? "Cobrança em preparo" : "—"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={5} />}
              {!loading && invoices.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    Nenhuma fatura por aqui ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Como pagar esta fatura */}
      <Dialog
        open={Boolean(pagando)}
        onOpenChange={(aberto) => !aberto && setPagando(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pagar {moeda(pagando?.value, pagando?.currency)}</DialogTitle>
            <DialogDescription>
              {pagando?.detail} — vence em {dataCurta(pagando?.dueDate)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {pagando?.pixQrCode && (
              <div className="flex justify-center rounded-lg bg-white p-4">
                <img
                  src={`data:image/png;base64,${pagando.pixQrCode}`}
                  alt="QR Code do Pix desta fatura"
                  className="h-52 w-52"
                />
              </div>
            )}

            {pagando?.pixPayload && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  Pix copia e cola
                </Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    readOnly
                    value={pagando.pixPayload}
                    className="font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Copiar código Pix"
                    onClick={() =>
                      copiar(pagando.pixPayload, "Código Pix copiado.")
                    }
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {pagando?.invoiceUrl && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() =>
                  window.open(pagando.invoiceUrl, "_blank", "noopener")
                }
              >
                <ExternalLink className="h-4 w-4" />
                {billing.gateway === "stripe"
                  ? "Escolher como pagar"
                  : "Abrir boleto / página de pagamento"}
              </Button>
            )}

            {billing.gateway === "stripe" && (
              <p className="text-xs text-muted-foreground">
                A página aceita todas as formas ligadas na conta — cartão, MB
                WAY, Multibanco e as demais. Se ela abrir oferecendo outra
                moeda, escolha o euro no topo: MB WAY e Multibanco só existem
                em euro.
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              O sistema reconhece o pagamento sozinho, em geral em poucos
              minutos. Não precisa mandar comprovante.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPagando(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </MainContainer>
  );
};

export default Billing;
